import { NextPage } from 'next';
import { createSelector, Selector, ParametricSelector } from 'reselect';
import { RootState } from '../';
import { Copyrights, Copyright } from './interfaces';
import { copyrightIdsSelector } from '../transactions/selectors';

export const copyrightsSelector: Selector<RootState, Copyrights> = ({ copyrights }) => {
  return copyrights.copyrights || {};
};

export const approvedIdsSelector: Selector<RootState, string[]> = ({ copyrights }) => {
  return copyrights.approvedIds || [];
};

export const pendingIdsSelector: Selector<RootState, string[]> = ({ copyrights }) => {
  return copyrights.pendingIds || [];
};

export const draftIdsSelector: Selector<RootState, string[]> = ({ copyrights }) => {
  return copyrights.draftIds || [];
};

export const rejectedIdsSelector: Selector<RootState, string[]> = ({ copyrights }) => {
  return copyrights.rejectedIds || [];
};

export const getApprovedCopyrights = () => {
  return createSelector<RootState, Copyrights, string[], Copyright[]>(
    [copyrightsSelector, approvedIdsSelector],
    (copyrights, copyrightIds) => {
      const _items: Copyright[] = [];

      copyrightIds.map((id) => {
        const copyright = copyrights[id];
        if (copyright) _items.push(copyright);
      });

      return _items;
    },
  );
};

export const getPendingCopyrights = () => {
  return createSelector<RootState, Copyrights, string[], string[], Copyright[]>(
    [copyrightsSelector, pendingIdsSelector, copyrightIdsSelector],
    (copyrights, copyrightIds, invoiceCopyrightIds) => {
      const _items: Copyright[] = [];

      copyrightIds.map((id) => {
        const copyright = copyrights[id];
        copyright.isInCart = invoiceCopyrightIds.includes(id);
        if (copyright) _items.push(copyright);
      });

      return _items;
    },
  );
};

export const getDraftCopyrights = () => {
  return createSelector<RootState, Copyrights, string[], string[], Copyright[]>(
    [copyrightsSelector, draftIdsSelector, copyrightIdsSelector],
    (copyrights, copyrightIds, invoiceCopyrightIds) => {
      const _items: Copyright[] = [];

      copyrightIds.map((id) => {
        const copyright = copyrights[id];
        copyright.isInCart = invoiceCopyrightIds.includes(id);
        if (copyright) _items.push(copyright);
      });

      return _items;
    },
  );
};

export const getRejectedCopyrights = () => {
  return createSelector<RootState, Copyrights, string[], Copyright[]>(
    [copyrightsSelector, rejectedIdsSelector],
    (copyrights, copyrightIds) => {
      const _items: Copyright[] = [];

      copyrightIds.map((id) => {
        const copyright = copyrights[id];
        if (copyright) _items.push(copyright);
      });

      return _items;
    },
  );
};

type PageProps = NextPage & {
  copyrightId?: string;
};

const copyrightSelector: ParametricSelector<RootState, PageProps, Copyright> = (
  { copyrights },
  props,
) => {
  const { copyrightId } = props;
  const copyright = copyrights.copyrights[copyrightId] || {};

  return copyright as Copyright;
};

export const getCopyright = () => {
  return createSelector<RootState, PageProps, Copyright, Copyright>(
    [copyrightSelector],
    (copyright: Copyright) => copyright,
  );
};

export const searchedIdsSelector: Selector<RootState, string[]> = ({ copyrights }) => {
  return copyrights.searchedIds || [];
};

export const getSearchedCopyrights = () => {
  return createSelector<RootState, Copyrights, string[], Copyright[]>(
    [copyrightsSelector, searchedIdsSelector],
    (copyrights, copyrightIds) => {
      const _items: Copyright[] = [];

      copyrightIds.map((id) => {
        const copyright = copyrights[id];
        if (copyright) _items.push(copyright);
      });

      return _items;
    },
  );
};

export const isFetchingSelector: Selector<RootState, boolean> = ({ copyrights }) => {
  return copyrights.isFetching || false;
};

export const getIsFetching = () => {
  return createSelector<RootState, boolean, boolean>(
    [isFetchingSelector],
    (isFetching) => isFetching,
  );
};

export const isDisputedSelector: Selector<RootState, boolean> = ({ copyrights }) => {
  return copyrights.disputed || false;
};

export const getIsDisputed = () => {
  return createSelector<RootState, boolean, boolean>(
    [isDisputedSelector],
    (isDisputed) => isDisputed,
  );
};

export const historicalCopyrightsSelector: Selector<RootState, Copyrights> = ({ copyrights }) => {
  return copyrights.historicalCopyrights || {};
};

export const historicalIdsSelector: Selector<RootState, string[]> = ({ copyrights }) => {
  return copyrights.historicalCopyrightIds || [];
};

export const getHistoricalCopyrights = () => {
  return createSelector<RootState, Copyrights, string[], Copyright[]>(
    [historicalCopyrightsSelector, historicalIdsSelector],
    (copyrights, copyrightIds) => {
      const _items: Copyright[] = [];

      copyrightIds.map((id) => {
        const copyright = copyrights[id];
        if (copyright) _items.push(copyright);
      });

      return _items;
    },
  );
};

const historicalCopyrightSelector: ParametricSelector<RootState, PageProps, Copyright> = (
  { copyrights },
  props,
) => {
  const { copyrightId } = props;
  const copyright = copyrights.historicalCopyrights[copyrightId] || {};

  return copyright as Copyright;
};

export const getHistoricalCopyright = () => {
  return createSelector<RootState, PageProps, Copyright, Copyright>(
    [historicalCopyrightSelector],
    (copyright: Copyright) => copyright,
  );
};
